// src/pages/actividades/components/agenda/AgendaTimeGrid.tsx
import { useRef, useEffect, useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ActivityBlock } from './ActivityBlock';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';
import { AgendaUnscheduled } from './AgendaUnscheduled';
import type { ActividadListItem } from '@/types';

const SLOT_HEIGHT = 40;     // px per 30min
const START_HOUR = 6;
const END_HOUR = 22;
const TOTAL_SLOTS = (END_HOUR - START_HOUR) * 2; // 32 slots
const TIME_LABEL_WIDTH = 48; // px for the time labels column

// ─── Helpers ───────────────────────────────────────────────────────────────

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

function formatDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const SHORT_DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

function slotToTime(slotIndex: number): string {
  const totalMin = START_HOUR * 60 + slotIndex * 30;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ─── Layout computation (pure function, not a hook) ────────────────────────

interface LayoutBlock {
  activity: ActividadListItem;
  top: number;
  height: number;
  column: number;
  totalColumns: number;
  overflow: boolean;
}

interface LayoutResult {
  scheduled: LayoutBlock[];
  unscheduled: ActividadListItem[];
}

interface TimeRange {
  startMin: number;
  endMin: number;
  activity: ActividadListItem;
}

function computeLayout(activities: ActividadListItem[], maxOverlapColumns?: number): LayoutResult {
  const scheduled: LayoutBlock[] = [];
  const unscheduled: ActividadListItem[] = [];
  const gridStartMin = START_HOUR * 60;
  const gridEndMin = END_HOUR * 60;

  const timeRanges: TimeRange[] = [];

  for (const act of activities) {
    if (!act.hora_agendada) {
      unscheduled.push(act);
      continue;
    }

    const [h, m] = act.hora_agendada.split(':').map(Number);
    const startMin = h * 60 + (m || 0);
    const duration = act.duracion_estimada_min ?? 30;
    const endMin = startMin + duration;

    // Exclude activities completely outside the visible range
    if (endMin <= gridStartMin || startMin >= gridEndMin) {
      unscheduled.push(act);
      continue;
    }

    timeRanges.push({ startMin, endMin, activity: act });
  }

  // Sort by start time
  timeRanges.sort((a, b) => a.startMin - b.startMin);

  if (timeRanges.length === 0) return { scheduled, unscheduled };

  // Build transitive clusters via sweep line
  const clusters: TimeRange[][] = [];
  let currentCluster: TimeRange[] = [timeRanges[0]];
  let clusterEnd = timeRanges[0].endMin;

  for (let i = 1; i < timeRanges.length; i++) {
    const range = timeRanges[i];
    if (range.startMin < clusterEnd) {
      currentCluster.push(range);
      clusterEnd = Math.max(clusterEnd, range.endMin);
    } else {
      clusters.push(currentCluster);
      currentCluster = [range];
      clusterEnd = range.endMin;
    }
  }
  clusters.push(currentCluster);

  // Assign columns per cluster using greedy algorithm
  for (const cluster of clusters) {
    const columnEnds: number[] = [];
    const assignments: { range: TimeRange; column: number }[] = [];

    for (const range of cluster) {
      let assigned = false;
      for (let col = 0; col < columnEnds.length; col++) {
        if (columnEnds[col] <= range.startMin) {
          columnEnds[col] = range.endMin;
          assignments.push({ range, column: col });
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        assignments.push({ range, column: columnEnds.length });
        columnEnds.push(range.endMin);
      }
    }

    const totalColumns = columnEnds.length;

    for (const { range, column } of assignments) {
      const visibleStart = Math.max(range.startMin, gridStartMin);
      const visibleEnd = Math.min(range.endMin, gridEndMin);
      const top = ((visibleStart - gridStartMin) / 30) * SLOT_HEIGHT;
      const height = Math.max(((visibleEnd - visibleStart) / 30) * SLOT_HEIGHT, SLOT_HEIGHT);
      const overflow = maxOverlapColumns != null && column >= maxOverlapColumns;

      scheduled.push({
        activity: range.activity,
        top,
        height,
        column,
        totalColumns,
        overflow,
      });
    }
  }

  return { scheduled, unscheduled };
}

// ─── Component ─────────────────────────────────────────────────────────────

interface Props {
  days: Date[];
  activities: ActividadListItem[];
  onSlotClick?: (date: Date, time: string) => void;
}

export function AgendaTimeGrid({ days, activities, onSlotClick }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isWeek = days.length > 1;

  // Group activities by date
  const activitiesByDate = useMemo(() => {
    const map = new Map<string, ActividadListItem[]>();
    for (const day of days) {
      map.set(formatDateKey(day), []);
    }
    for (const act of activities) {
      if (act.fecha_agendada) {
        const key = act.fecha_agendada;
        const existing = map.get(key);
        if (existing) {
          existing.push(act);
        }
      }
    }
    return map;
  }, [days, activities]);

  // Compute layouts per day
  const layoutsByDate = useMemo(() => {
    const result = new Map<string, LayoutResult>();
    for (const [dateKey, dayActivities] of activitiesByDate) {
      result.set(dateKey, computeLayout(dayActivities, isWeek ? 2 : undefined));
    }
    return result;
  }, [activitiesByDate, isWeek]);

  // Collect all unscheduled activities across days
  const allUnscheduled = useMemo(() => {
    if (isWeek) {
      const perDay = new Map<string, ActividadListItem[]>();
      for (const [dateKey, layout] of layoutsByDate) {
        perDay.set(dateKey, layout.unscheduled);
      }
      // Activities without fecha_agendada go in first day column
      for (const act of activities) {
        if (!act.fecha_agendada) {
          const firstKey = formatDateKey(days[0]);
          const arr = perDay.get(firstKey) ?? [];
          arr.push(act);
          perDay.set(firstKey, arr);
        }
      }
      return perDay;
    }
    // Day view: single list
    const dayKey = formatDateKey(days[0]);
    const layout = layoutsByDate.get(dayKey);
    const noDate = activities.filter((a) => !a.fecha_agendada);
    return new Map([[dayKey, [...(layout?.unscheduled ?? []), ...noDate]]]);
  }, [layoutsByDate, activities, days, isWeek]);

  // Compute max unscheduled height for uniform week row
  const unscheduledMaxHeight = useMemo(() => {
    let maxCount = 0;
    for (const [, items] of allUnscheduled) {
      maxCount = Math.max(maxCount, items.length);
    }
    if (maxCount === 0) return 0;
    return Math.min(Math.max(32, maxCount * 26), 80);
  }, [allUnscheduled]);

  // Auto-scroll to current time when today becomes visible
  const prevHadTodayRef = useRef(false);
  useEffect(() => {
    const hasToday = days.some(isToday);

    // Reset scroll tracking when we navigate away from today
    if (!hasToday) {
      prevHadTodayRef.current = false;
      return;
    }

    // Skip if we already scrolled and didn't navigate away
    if (prevHadTodayRef.current) return;
    if (!scrollRef.current) return;

    const now = new Date();
    const currentMin = now.getHours() * 60 + now.getMinutes();
    const gridStartMin = START_HOUR * 60;
    const top = ((currentMin - gridStartMin) / 30) * SLOT_HEIGHT;
    const containerHeight = scrollRef.current.clientHeight;

    scrollRef.current.scrollTop = Math.max(0, top - containerHeight / 2);
    prevHadTodayRef.current = true;
  }, [days]);

  const handleSlotClick = useCallback(
    (dayIndex: number, slotIndex: number) => {
      if (!onSlotClick) return;
      onSlotClick(days[dayIndex], slotToTime(slotIndex));
    },
    [onSlotClick, days]
  );

  // Overflow badges for week view
  const overflowByDate = useMemo(() => {
    const result = new Map<string, { count: number; top: number; height: number; totalColumns: number }>();
    for (const [dateKey, layout] of layoutsByDate) {
      const overflows = layout.scheduled.filter((b) => b.overflow);
      if (overflows.length > 0) {
        result.set(dateKey, {
          count: overflows.length,
          top: overflows[0].top,
          height: overflows[0].height,
          totalColumns: overflows[0].totalColumns,
        });
      }
    }
    return result;
  }, [layoutsByDate]);

  return (
    <div className="border rounded-lg overflow-hidden bg-background">
      {/* Week day headers */}
      {isWeek && (
        <div className="flex border-b bg-muted/30" style={{ paddingLeft: `${TIME_LABEL_WIDTH}px` }}>
          {days.map((day) => (
            <div
              key={formatDateKey(day)}
              className={cn(
                'flex-1 text-center py-1.5 text-xs font-medium border-l first:border-l-0',
                isToday(day) ? 'bg-blue-50/50 text-blue-700 font-semibold' : 'text-muted-foreground'
              )}
            >
              {SHORT_DAY_NAMES[day.getDay()]} {day.getDate()}
            </div>
          ))}
        </div>
      )}

      {/* Unscheduled section */}
      {unscheduledMaxHeight > 0 && (
        isWeek ? (
          <div className="flex border-b" style={{ paddingLeft: `${TIME_LABEL_WIDTH}px` }}>
            {days.map((day) => {
              const key = formatDateKey(day);
              const items = allUnscheduled.get(key) ?? [];
              return (
                <div
                  key={key}
                  className="flex-1 border-l first:border-l-0"
                  style={{ minHeight: `${unscheduledMaxHeight}px` }}
                >
                  {items.length > 0 && (
                    <AgendaUnscheduled activities={items} maxHeight={unscheduledMaxHeight} />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <AgendaUnscheduled
            activities={allUnscheduled.get(formatDateKey(days[0])) ?? []}
            maxHeight={80}
          />
        )
      )}

      {/* Time grid with scroll */}
      <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
        <div className="relative flex" style={{ height: `${TOTAL_SLOTS * SLOT_HEIGHT}px` }}>
          {/* Time labels */}
          <div className="shrink-0 relative" style={{ width: `${TIME_LABEL_WIDTH}px` }}>
            {Array.from({ length: TOTAL_SLOTS }, (_, i) => {
              if (i % 2 !== 0) return null; // Only show full-hour labels
              return (
                <div
                  key={i}
                  className="absolute right-2 text-[10px] text-muted-foreground -translate-y-1/2"
                  style={{ top: `${i * SLOT_HEIGHT}px` }}
                >
                  {slotToTime(i)}
                </div>
              );
            })}
          </div>

          {/* Day columns */}
          <div className="flex flex-1 relative">
            {days.map((day, dayIndex) => {
              const dateKey = formatDateKey(day);
              const layout = layoutsByDate.get(dateKey);
              const overflow = overflowByDate.get(dateKey);
              const todayCol = isToday(day);

              return (
                <div
                  key={dateKey}
                  className={cn(
                    'flex-1 relative border-l first:border-l-0',
                    todayCol && isWeek && 'bg-blue-50/20'
                  )}
                >
                  {/* Slot grid lines + click targets */}
                  {Array.from({ length: TOTAL_SLOTS }, (_, i) => (
                    <div
                      key={i}
                      className={cn(
                        'absolute w-full border-b border-gray-100',
                        i % 2 === 0 && 'border-gray-200',
                        onSlotClick && 'cursor-pointer hover:bg-muted/40 transition-colors'
                      )}
                      style={{ top: `${i * SLOT_HEIGHT}px`, height: `${SLOT_HEIGHT}px` }}
                      onClick={() => handleSlotClick(dayIndex, i)}
                    />
                  ))}

                  {/* Activity blocks */}
                  <div className="absolute inset-0 px-0.5">
                    {layout?.scheduled
                      .filter((b) => !b.overflow)
                      .map((block) => (
                        <ActivityBlock
                          key={block.activity.id}
                          activity={block.activity}
                          top={block.top}
                          height={block.height}
                          column={block.column}
                          totalColumns={block.totalColumns}
                        />
                      ))}

                    {/* Overflow badge for week view */}
                    {overflow && (
                      <div
                        className="absolute bg-gray-200 text-gray-700 text-[10px] font-medium rounded px-1.5 py-0.5 cursor-pointer hover:bg-gray-300 transition-colors"
                        style={{
                          top: `${overflow.top}px`,
                          height: `${overflow.height}px`,
                          right: 0,
                          width: `${100 / overflow.totalColumns}%`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="Ver mas en vista dia"
                      >
                        +{overflow.count}
                      </div>
                    )}
                  </div>

                  {/* Current time indicator (only in today's column) */}
                  {todayCol && (
                    <CurrentTimeIndicator
                      startHour={START_HOUR}
                      endHour={END_HOUR}
                      slotHeight={SLOT_HEIGHT}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
